import React, { FC, useMemo } from 'react'
import { useTable, useExpanded, Row, useSortBy, HeaderGroup } from 'react-table'
import Font from '../../Core/Typography/Font/Font'
import {
  TableContainer,
  TableHeaderCell,
  TableRow,
  TableCell,
  TableRowToModal,
  TableBody,
  TableHead,
} from './Table.styles'
import { TableProps } from './types'
import Icon from 'src/components/Core/Iconography/Icon/Icon'
import Flex from 'src/components/Layout/Flex/Flex'

const Table: FC<TableProps> = props => {
  const {
    version,
    tableData,
    tableColumns,
    removeCellEllipsis,
    cellTag,
    onSortByColumn,
    formatRowProps,
    formatCellProps,
    headerCellMaxWidth,
    bodyCellMaxWidth,
    fixedColumnsWidth,
    sortTypes,
    tableCellPadding
  } = props

  const data = useMemo(() => tableData, [tableData])

  const columns = useMemo(() => tableColumns, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns
  } = useTable(
    // @ts-ignore:next-line
    { columns, data, sortTypes },
    useSortBy,
    useExpanded,
  )

  if (tableColumns?.length === 0 || tableData?.length === 0) return null

  const handleHeaderClick = (column: HeaderGroup) => {
    // @ts-ignore
    if(column.sortable) {
      // @ts-ignore
      sortTypes ? column.toggleSortBy() : column.toggleSortBy(!column.isSortedDesc)
      // @ts-ignore
      if (onSortByColumn) onSortByColumn(column.id, column.isSortedDesc)
    }
  }

  return (
    <>
      <TableContainer {...getTableProps()} tableReadOnly={props.tableReadOnly}>
        <TableHead addScrollToTableBody={props?.addScrollToTableBody} fixedColumnsWidth={fixedColumnsWidth}>
          {headerGroups.map((headerGroup, headerGroupIndex) => (
            <tr key={headerGroupIndex} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => (
                <TableHeaderCell
                  key={index}
                  version={version}
                  {
                    // @ts-ignore
                    ...column.getHeaderProps(column.getSortByToggleProps())
                  }
                  onClick={() => handleHeaderClick(column)}
                  maxWidth={headerCellMaxWidth}
                >
                  {/* @ts-ignore */}
                  <Flex justifyContent={props.thChildrenJustifyContent || column?.textAlign || 'flex-start'}>
                    <Font tag={version === 2 ? 'h5' : 'h6'}>
                      {column.render('Header')}
                    </Font>
                    {// @ts-ignore
                      column.sortable && (
                        <Icon name='sort' size='xxs' color='dark' />
                      )
                    }
                  </Flex>
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </TableHead>
        <TableBody
          {...getTableBodyProps()}
          addScrollToTableBody={props?.addScrollToTableBody}
        >
          {rows.map((row: Row<{ [key: string]: object }>, rowIndex: number) => {
            prepareRow(row)
            return (
              <TableRow
                key={rowIndex}
                lastGrey={props.lastGrey && rowIndex === rows.length - 1}
                {...row.getRowProps(formatRowProps && formatRowProps(row))}
                selected={rowIndex === props?.selectedRowIndex}
                addScrollToTableBody={props?.addScrollToTableBody}
              >
                {row.cells.map((cell, index: number) => {
                  return cell?.value?.type?.name === 'Icon' ||
                    cell?.value?.props?.children?.type?.name === 'Icon' ? (
                      <TableCell
                        key={index}
                        {...cell.getCellProps(formatCellProps && formatCellProps(cell))}
                        iconCell
                        fixedColumnsWidth={fixedColumnsWidth}
                        tableCellPadding={tableCellPadding}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    ) : (typeof cell.render('Cell') === 'string' ? (
                      <TableCell
                        key={index}
                        {...cell.getCellProps(formatCellProps && formatCellProps(cell))}
                        maxWidth={bodyCellMaxWidth}
                        tableCellPadding={tableCellPadding}
                      >
                        <Font
                          tag={cellTag || 'h6'}
                          bold
                          ellipsis={removeCellEllipsis ? false : true}
                        >
                          {cell.render('Cell')}
                        </Font>
                      </TableCell>
                    ) : (
                      <TableCell
                        key={index}
                        {...cell.getCellProps(formatCellProps && formatCellProps(cell))}
                        maxWidth={bodyCellMaxWidth}
                        tableCellPadding={tableCellPadding}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    ))
                })}
                {/* @ts-ignore */}
                {row.isExpanded && (
                  <TableRowToModal>
                    <td colSpan={visibleColumns.length}>
                      {props.renderRowSubComponent({ row })}
                    </td>
                  </TableRowToModal>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </TableContainer>
    </>
  )
}

Table.defaultProps = {
  lastGrey: false,
  version: 1
}

export default Table
